import React, { useEffect, useRef, useState } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import {
  IconSearchBox,
  filterIcons,
  iconDataForObject,
  iconComponent,
  iconSetForObject,
  tranlsateIconData,
  isFaIcon,
} from '../utils/icons';
import { type IconTabItem } from '../toolbars/ObjectMenu/IconsTab';
import styles from '../styles.module.css';
import useAsync from '../components/hooks/useAsync';
import { type Instance as TippyInstance } from 'tippy.js/index';

const Popup: React.FC<{
  currentIcon: IconTabItem | null | undefined;
  setCurrentIcon: (icon: IconTabItem | null | undefined) => void;
  selection: fabric.Object;
  tippyRef: React.MutableRefObject<TippyInstance | undefined>;
}> = ApplicationView(({ currentIcon, setCurrentIcon, selection, tippyRef }) => {
  const controller = useController(WhiteboardController);
  const [filterText, setFilterText] = useState('');
  const { data: allIcons } = useAsync(async () => {
    return await iconSetForObject(selection);
  });

  useEffect(() => {
    if (allIcons) {
      // Fixes the position of the tippy popper once there is content
      tippyRef.current?.popperInstance?.update();
    }
  }, [allIcons, tippyRef]);

  const setIcon = (iconData: IconTabItem) => {
    setCurrentIcon(iconData);
    selection.setOnObjects('iconName', tranlsateIconData(iconData));
    controller.canvas.requestRenderAll();
    Tool.toolActionComplete({ controller });
  };

  let icons: IconTabItem[] = [];
  if (filterText.length > 0 && allIcons) {
    icons = filterIcons(allIcons, filterText);
  } else if (allIcons) {
    icons = allIcons;
  }

  const columnCount = !currentIcon || isFaIcon(currentIcon) ? 8 : 6;
  const columnWidth = !currentIcon || isFaIcon(currentIcon) ? 40 : 48;

  return (
    <IconSearchBox
      value={filterText}
      onChange={value => setFilterText(value)}
      style={{ width: '95%', margin: '8px' }}
      placeholder='Search for an icon'
      onClick={iconData => setIcon(iconData)}
      icons={icons}
      currentIcon={currentIcon}
      columns={columnCount}
      columnWidth={columnWidth}
      controller={controller}
    />
  );
});

class ToolChooseIcon extends Tool {
  static toolName = 'Icon';

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool }) => {
        const [currentIcon, setCurrentIcon] = useState<IconTabItem | null>();
        const tippyRef = useRef<TippyInstance>();

        useEffect(() => {
          void iconDataForObject(selection.firstObject()).then(icon => {
            setCurrentIcon(icon);
          });
        }, [selection]);

        return (
          <ToolButton
            className={styles.iconToolbarButton}
            popup={
              <Popup
                currentIcon={currentIcon}
                setCurrentIcon={setCurrentIcon}
                selection={selection}
                tippyRef={tippyRef}
              />
            }
            tippyRef={tippyRef}
            tool={tool}
          >
            {iconComponent(currentIcon)}
          </ToolButton>
        );
      }
    );
    return Button;
  }
}

export default ToolChooseIcon;
